import { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import api from '@/lib/api';

// Signer identity is SERVER-derived: participant from the assignment, representative from the participant's contact
// record, staff/witness from the logged-in user. The client only chooses a capacity (and a contact for representatives).
const CAPACITIES = [
  { value: 'PARTICIPANT', label: 'Participant (from assignment)' },
  { value: 'GUARDIAN', label: 'Guardian (participant contact)' },
  { value: 'NOMINEE', label: 'Nominee (participant contact)' },
  { value: 'ADVOCATE', label: 'Advocate — not yet supported', disabled: true },
  { value: 'STAFF', label: 'Staff member (you)' },
  { value: 'WITNESS', label: 'Witness (you — staff only)' },
];
const REPRESENTATIVE = ['GUARDIAN', 'NOMINEE'];

export const SignaturePad = ({ fieldKey, label, attestationText, attestationVersion, participantId, disabled, onCapture, existing, onReplace, onCancelReplace }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const [capacity, setCapacity] = useState('PARTICIPANT');
  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState('');
  const [assisted, setAssisted] = useState(false);
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const replacing = !!existing?.replacing;
  const isRep = REPRESENTATIVE.includes(capacity);
  const isStaff = ['STAFF', 'WITNESS'].includes(capacity);

  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d');
    const ratio = window.devicePixelRatio || 1;
    const w = c.clientWidth, h = 180;
    c.width = w * ratio; c.height = h * ratio; ctx.scale(ratio, ratio);
    ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = '#111';
  }, [existing]);

  useEffect(() => {
    if (!isRep || !participantId) { setContacts([]); return; }
    api.get(`/engagement/contacts/by-participant/${participantId}`)
      .then(({ data }) => setContacts((data.items || data.data || []).filter(c => c.relationship === capacity && c.status === 'ACTIVE')))
      .catch(() => setContacts([]));
    setContactId('');
  }, [isRep, capacity, participantId]);

  const pos = (e) => { const r = canvasRef.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const down = (e) => { if (disabled) return; drawing.current = true; const ctx = canvasRef.current.getContext('2d'); const [x, y] = pos(e); ctx.beginPath(); ctx.moveTo(x, y); canvasRef.current.setPointerCapture?.(e.pointerId); };
  const move = (e) => { if (!drawing.current) return; const ctx = canvasRef.current.getContext('2d'); const [x, y] = pos(e); ctx.lineTo(x, y); ctx.stroke(); setHasInk(true); };
  const up = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); setHasInk(false); };
  const canSave = hasInk && (!isRep || contactId) && (!assisted || note.trim()) && (!replacing || reason.trim());

  const capture = async () => {
    if (!canSave) return;
    setBusy(true);
    try {
      const blob = await new Promise(res => canvasRef.current.toBlob(res, 'image/png'));
      await onCapture({ blob, signerCapacity: capacity, signerContactId: isRep ? contactId : null, assistedByMe: !isStaff && assisted, assistanceNote: !isStaff && assisted ? note.trim() : null, attestationVersion, replacementReason: replacing ? reason.trim() : null });
      clear();
    } finally { setBusy(false); }
  };

  if (existing && !existing.replacing) {
    return (
      <div className="border rounded-md p-3 bg-muted/20 text-sm" data-testid={`signature-captured-${fieldKey}`}>
        <p className="font-medium">Signed by {existing.signer_display_name} ({existing.signer_capacity.toLowerCase()})</p>
        <p className="text-xs text-muted-foreground">Drawn signature · {existing.signed_at} · attestation {existing.attestation_version}</p>
        {existing.authority_verification_status && <p className="text-xs text-amber-700" data-testid={`signature-authority-${fieldKey}`}>Representative authority: {existing.authority_verification_status.replace(/_/g, ' ').toLowerCase()}</p>}
        {existing.assisted_by_user_id && <p className="text-xs text-muted-foreground">Assisted capture recorded (staff did not sign on behalf).</p>}
        {onReplace && !disabled && <Button type="button" size="sm" variant="outline" className="mt-2" onClick={onReplace} data-testid={`signature-replace-${fieldKey}`}>Replace signature</Button>}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={`signature-pad-${fieldKey}`}>
      <p className="text-xs text-muted-foreground border-l-2 pl-2" data-testid={`signature-attestation-${fieldKey}`}>{attestationText} <span className="opacity-70">(attestation {attestationVersion})</span></p>
      <canvas ref={canvasRef} role="img" aria-label={`${label} signature drawing area`} tabIndex={0}
        className="w-full h-[180px] border rounded-md bg-white touch-none cursor-crosshair"
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} onPointerCancel={up}
        data-testid={`signature-canvas-${fieldKey}`} />
      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <Label className="text-xs" htmlFor={`sig-cap-${fieldKey}`}>Signing as</Label>
          <select id={`sig-cap-${fieldKey}`} className="w-full border rounded-md h-9 px-2 text-sm" value={capacity} onChange={e => setCapacity(e.target.value)} disabled={disabled} data-testid={`signature-capacity-${fieldKey}`}>
            {CAPACITIES.map(c => <option key={c.value} value={c.value} disabled={c.disabled}>{c.label}</option>)}
          </select>
        </div>
        {isRep && (
          <div>
            <Label className="text-xs" htmlFor={`sig-contact-${fieldKey}`}>Representative (active {capacity.toLowerCase()} contact)</Label>
            <select id={`sig-contact-${fieldKey}`} className="w-full border rounded-md h-9 px-2 text-sm" value={contactId} onChange={e => setContactId(e.target.value)} disabled={disabled} data-testid={`signature-contact-${fieldKey}`}>
              <option value="">{contacts.length ? 'Select contact…' : `No active ${capacity.toLowerCase()} contact on file`}</option>
              {contacts.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}{c.relationshipDetail ? ` · ${c.relationshipDetail}` : ''}</option>)}
            </select>
          </div>
        )}
      </div>
      {isRep && <p className="text-[11px] text-amber-700">Representative authority is recorded as “document on file” or “manual verification required” — never as verified.</p>}
      {capacity === 'WITNESS' && <p className="text-[11px] text-muted-foreground">Witness records you (the logged-in staff member). External witnesses are not supported.</p>}
      {!isStaff && (
        <div className="text-xs space-y-1">
          <label className="flex items-center gap-2"><input type="checkbox" checked={assisted} onChange={e => setAssisted(e.target.checked)} disabled={disabled} data-testid={`signature-assisted-${fieldKey}`} /> I (staff) assisted the signer with capture</label>
          {assisted && <input className="w-full border rounded-md h-8 px-2" placeholder="How you assisted (required)" value={note} onChange={e => setNote(e.target.value)} data-testid={`signature-assist-note-${fieldKey}`} />}
        </div>
      )}
      {replacing && (
        <div>
          <Label className="text-xs" htmlFor={`sig-reason-${fieldKey}`}>Reason for replacing (required, audited — the previous signature is retained as superseded evidence)</Label>
          <input id={`sig-reason-${fieldKey}`} className="w-full border rounded-md h-8 px-2 text-xs" value={reason} onChange={e => setReason(e.target.value)} data-testid={`signature-replace-reason-${fieldKey}`} />
        </div>
      )}
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={clear} disabled={disabled || !hasInk} data-testid={`signature-clear-${fieldKey}`}>Clear</Button>
        <Button type="button" size="sm" onClick={capture} disabled={disabled || busy || !canSave} data-testid={`signature-save-${fieldKey}`}>{busy ? 'Saving…' : existing?.replacing ? 'Save replacement' : 'Save signature'}</Button>
        {existing?.replacing && onCancelReplace && <Button type="button" variant="ghost" size="sm" onClick={onCancelReplace} data-testid={`signature-cancel-replace-${fieldKey}`}>Cancel</Button>}
      </div>
      <p className="text-[11px] text-muted-foreground">Drawn signature is captured as evidence with a server-verified signer, capacity and server time. It is not a certified e-signature service.</p>
    </div>
  );
};
